import { useRouter } from "@/_router";
import BackgroundAmbience from "@/components/BackgroundAmbience";
import { CheckIcon } from "@/components/Icons";
import { actions, useAppStore } from "@/controller";
import { useAuth } from "@/models/useAuth";
import { parseMarketingOnboardingContext } from "@/onboarding-interactions";
import { isNativeApp } from "@/platform";
import { logClientEvent } from "@/telemetry";
import type { SubscriptionPlan } from "@/types";
import { useUISlice } from "@/models/useUISlice";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-hot-toast";

const resolveCheckoutPlan = (
  requestedPlan: string | null,
): SubscriptionPlan => {
  const { monthly, annual } = useAppStore.getState().webSubscriptionPlans;
  const normalizedPlan = requestedPlan?.toLowerCase();

  if (!normalizedPlan || normalizedPlan === "monthly") return monthly;
  if (normalizedPlan === "annual" || normalizedPlan === "yearly") {
    return annual;
  }
  if (requestedPlan === monthly || requestedPlan === annual) {
    return requestedPlan as SubscriptionPlan;
  }

  return monthly;
};

/**
 * The web purchase entry point. A durable device identity owns checkout and
 * is upgraded in place whenever the customer later verifies an account.
 */
export default function WebCheckoutEntry() {
  const router = useRouter();
  const { t } = useTranslation();

  useEffect(() => {
    if (isNativeApp()) {
      useUISlice.getState().setIsNewSession(true);
      router.replace("/home");
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const requestedPlan =
      urlParams.get("plan") ||
      urlParams.get("subscriptionPlan") ||
      urlParams.get("package");
    const marketingContext = parseMarketingOnboardingContext(
      window.location.search,
    );

    if (marketingContext.persona) {
      useAppStore.getState().setOnboardingPersona(marketingContext.persona);
    }
    if (marketingContext.intent) {
      useAppStore.getState().setOnboardingIntent(marketingContext.intent);
    }
    if (marketingContext.persona || marketingContext.intent) {
      logClientEvent("acquisition.contextApplied", {
        intent: marketingContext.intent,
        persona: marketingContext.persona,
        source: "marketing_site",
      });
    }

    let disposed = false;
    // Device identity and remote pricing are independent. Starting config
    // now keeps the checkout handoff to one visible wait instead of two.
    const remoteConfig = actions.user.remoteConfig();
    const fail = () => {
      if (disposed) return;
      toast.error(t("actions.subscriptionError"));
      router.replace("/home");
    };

    void useAuth.signInAsGuest({
      onError: fail,
      callback: async () => {
        if (disposed) return;
        await remoteConfig;
        if (disposed) return;

        const returnUrl = new URL("/home", window.location.origin);

        const result = await actions.subscription.stripeCheckout({
          subscriptionPlan: resolveCheckoutPlan(requestedPlan),
          baseAppUrl: returnUrl.toString(),
        });
        if ("error" in result) {
          fail();
          return;
        }
        if (!disposed) window.open(result.value, "_self");
      },
    });

    return () => {
      disposed = true;
    };
  }, [router, t]);

  return (
    <main className="app-page-background fixed inset-0 bg-[var(--bg-primary)] text-[var(--text-primary)] flex items-center justify-center overflow-hidden">
      <BackgroundAmbience />

      <div
        className="relative z-10 flex max-w-sm flex-col items-center px-6 text-center"
        role="status"
        aria-live="polite"
      >
        <img
          src="/logo.png"
          alt="1Transcribe"
          className="mb-5 h-16 w-16 rounded-2xl shadow-lg"
        />
        <div
          className="mb-5 h-9 w-9 animate-spin rounded-full border-4 border-[var(--fill-3)] border-t-[var(--accent)] motion-reduce:animate-none"
          aria-hidden="true"
        />
        <h1 className="mb-2 text-[22px] font-bold tracking-tight">
          {t("checkoutPage.title")}
        </h1>
        <p className="mb-6 text-[14px] leading-relaxed text-[var(--text-secondary)]">
          {t("checkoutPage.subtitle")}
        </p>
        <div className="flex items-center gap-2 text-[12px] font-semibold text-[var(--text-muted)]">
          <CheckIcon size={13} className="text-[var(--success)]" />
          <span>{t("checkoutPage.trustLine")}</span>
        </div>
      </div>
    </main>
  );
}
